/**
 * Header interactions: mobile menu open/close, submenu expand/collapse and the
 * language dropdown.
 *
 * Auto-inits from app.ts on `[data-header]`.
 */
export default class Header {
    constructor(header) {
        this.header = header;
        this.toggle = header.querySelector('[data-menu-toggle]');
        this.mobileMenu = header.querySelector('[data-mobile-menu]');
        this.langSwitcher = header.querySelector('[data-lang-switcher]');
        this.init();
    }
    init() {
        if (this.toggle && this.mobileMenu) {
            this.toggle.addEventListener('click', () => this.toggleMobileMenu());
        }
        this.initLangSwitcher();
        this.initSubmenus();
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') {
                return;
            }
            if (this.header.classList.contains('is-menu-open')) {
                this.closeMobileMenu();
            }
            this.closeSubmenus();
            this.closeLangSwitcher(true);
        });
        // A dropdown left open behind a click elsewhere on the page.
        document.addEventListener('click', (event) => {
            if (!this.header.contains(event.target)) {
                this.closeSubmenus();
            }
        });
    }
    /**
     * Submenu chevrons, shared by the desktop dropdowns and the mobile accordion.
     *
     * CSS opens a desktop panel on hover and `:focus-within` too; this covers the click
     * path and is the only thing that keeps `aria-expanded` truthful.
     */
    initSubmenus() {
        this.header.querySelectorAll('.menu-arrow').forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                const parent = button.closest('.menu-item-has-children');
                if (!parent) {
                    return;
                }
                const willOpen = !parent.classList.contains('is-open');
                // One at a time — opening a second dropdown while the first is still
                // down leaves two overlapping panels.
                this.closeSubmenus(parent);
                parent.classList.toggle('is-open', willOpen);
                button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
            });
        });
    }
    /** Close every open submenu, optionally sparing one. */
    closeSubmenus(except) {
        this.header.querySelectorAll('.menu-item-has-children.is-open').forEach((item) => {
            var _a;
            if (item === except) {
                return;
            }
            item.classList.remove('is-open');
            (_a = item.querySelector('.menu-arrow')) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
        });
    }
    /**
     * Language dropdown. WPML's own click-dropdown script is not enqueued, so the
     * open/close is ours: the panel ships with `hidden` and only this removes it.
     */
    initLangSwitcher() {
        const switcher = this.langSwitcher;
        if (!switcher) {
            return;
        }
        const toggle = switcher.querySelector('[data-lang-toggle]');
        const menu = switcher.querySelector('[data-lang-menu]');
        if (!toggle || !menu) {
            return;
        }
        toggle.addEventListener('click', (event) => {
            event.stopPropagation();
            if (switcher.classList.contains('is-open')) {
                this.closeLangSwitcher();
                return;
            }
            switcher.classList.add('is-open');
            toggle.setAttribute('aria-expanded', 'true');
            menu.removeAttribute('hidden');
        });
        document.addEventListener('click', (event) => {
            if (!switcher.contains(event.target)) {
                this.closeLangSwitcher();
            }
        });
    }
    /** @param returnFocus move focus back to the toggle (closing via Escape). */
    closeLangSwitcher(returnFocus = false) {
        var _a;
        const switcher = this.langSwitcher;
        if (!switcher || !switcher.classList.contains('is-open')) {
            return;
        }
        const toggle = switcher.querySelector('[data-lang-toggle]');
        switcher.classList.remove('is-open');
        toggle === null || toggle === void 0 ? void 0 : toggle.setAttribute('aria-expanded', 'false');
        (_a = switcher.querySelector('[data-lang-menu]')) === null || _a === void 0 ? void 0 : _a.setAttribute('hidden', '');
        if (returnFocus) {
            toggle === null || toggle === void 0 ? void 0 : toggle.focus();
        }
    }
    toggleMobileMenu() {
        if (this.header.classList.contains('is-menu-open')) {
            this.closeMobileMenu();
        }
        else {
            this.openMobileMenu();
        }
    }
    openMobileMenu() {
        var _a;
        this.header.classList.add('is-menu-open');
        document.body.classList.add('no-scroll');
        (_a = this.toggle) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'true');
    }
    closeMobileMenu() {
        var _a;
        this.header.classList.remove('is-menu-open');
        document.body.classList.remove('no-scroll');
        (_a = this.toggle) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-expanded', 'false');
    }
}
